import React, { type JSX } from 'react';
import styles from './Button.module.scss';
import { Button as PrimeButton, type ButtonProps as PrimeButtonProps } from 'primereact/button';

/** Defines the possible types for the button. */
export type Buttontype = 'Primary' | 'Secondary' | 'Tertiary';

/** Defines the variant for button icon. */
export type IconVariant = 'Outline' | 'Filled';
interface ImageAsset {
  name: string;
  path: string;
  variant: 'Outline' | 'Filled';
}

// Dynamically import all icons from a folder
const imageModules = import.meta.glob(
  '../../../assets/Icons/Library/*.{png,jpg,jpeg,svg}',
  {
    eager: true,
    import: 'default',
  }
);

const images: ImageAsset[] = Object.entries(imageModules).map(([path, mod]: [string, unknown]) => {
  const filename = path.replace(/^.*[\\/]/, '').replace(/\.\w+$/, '');

  return {
    name: filename.replace(/_(outline|filled)/, ''),
    variant: filename.includes('_filled') ? 'Filled' : 'Outline',
    path: mod as string,
  };
});

/**
 * Props for the Button component.
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  buttonType: Buttontype;
  state: string;
  backgroundColor?: string;
  size: string;
  disabled?: boolean;
  theme?: string;
  classType: string;
  buttonPrefix?: boolean;
  prefixInstance?: string;
  variant?: IconVariant;
  suffix?: boolean;
  suffixInstance?: string;
  text?: boolean;
  label?: React.ReactNode;
  buttonClass?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  buttonProps?: Partial<PrimeButtonProps>;
}

/**
 * Custom Button component using PrimeReact's Button. It supports prefix/suffix icons, theme-based shapes, and multiple variants.
 *
 * ## Import path
 * ```tsx
 * import { Button } from '@kpmg-global-audit-emu/react-ui-kit/atoms/Button';
 * ```
 * @param {ButtonProps} props - ButtonProps passed to the component including type, state, size, theme, and optional icons or label.
 * @returns {JSX.Element} A styled PrimeReact Button element.
 */
export const Button: React.FC<Readonly<ButtonProps>> = ({
  buttonType,
  state,
  size,
  theme,
  classType,
  buttonPrefix,
  prefixInstance,
  variant,
  suffix,
  suffixInstance,
  text,
  label,
  buttonClass,
  onClick,
  buttonProps,
  ...props
}: ButtonProps): JSX.Element => {
  const prefixIcon = buttonPrefix
    ? images.find((img) => img.name === prefixInstance && img.variant === (variant || 'Outline'))
      ?.path
    : null;
  const suffixIcon = suffix
    ? images.find((img) => img.name === suffixInstance && img.variant === (variant || 'Outline'))
      ?.path
    : null;

  const shapeClass = theme === 'Rounded' ? 'button--circle' : 'button--square';
  const buttonShapeClass = theme === 'Rounded' ? 'btn-shape-circle' : 'btn-shape-square';

  const typeToSeverityMap: Record<Buttontype, 'success' | 'info' | 'secondary'> = {
    Primary: 'success',
    Secondary: 'info',
    Tertiary: 'secondary',
  };
  return (
    <PrimeButton
      {...buttonProps}
      severity={typeToSeverityMap[buttonType]}
      className={[
        styles['button'],
        styles[`button--${size.toLowerCase()}`],
        styles[`button--${buttonType.toLowerCase()}`],
        styles[shapeClass],
        styles[buttonShapeClass],
        styles[state],
        styles[classType],
        buttonClass,
        buttonProps?.className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      {...props}
      data-button-type={buttonType?.toLowerCase()}
      data-button-state={state?.toLowerCase()}
    >
      {buttonPrefix && prefixIcon && (
        <img src={prefixIcon} alt={prefixInstance} className={styles.icon} />
      )}
      {text && <label> {label}</label>}
      {suffix && suffixIcon && (
        <img src={suffixIcon} alt={suffixInstance} className={styles.icon} />
      )}
    </PrimeButton>
  );
};
